import React from "react";
import { SkRect, SkEllipse, SkCircle, SkLine, SkPolygon } from "../prim";

/**
 * Anivaze v3 — skin-aware ACTORS (rebuilt on primitives).
 *
 * Each actor is `actor(x, y, t, scale?, …opts) → JSX`, composed ONLY from the
 * skinnable primitives in `../prim`. Because the primitives read the active skin
 * from context, EVERY actor automatically renders in Studio / Hand-drawn / Flat /
 * Doodle — no per-style code. Animate via `<g transform>` (translate/rotate) so
 * the hand-drawn seeds stay stable. This file is the REFERENCE pattern for agents
 * rebuilding the rest of the library. See design-plan/v3/PLAN.md §4–5.
 */

export function skSun(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const rot = (t * 14) % 360;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform={`rotate(${rot})`}>
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i / 8) * Math.PI * 2;
          return <SkLine key={i} x1={Math.cos(a) * 30} y1={Math.sin(a) * 30} x2={Math.cos(a) * 42} y2={Math.sin(a) * 42} stroke="#f59e0b" seed={700 + i} />;
        })}
      </g>
      <SkCircle cx={0} cy={0} r={24} fill="#fbbf24" seed={701} />
    </g>
  );
}

export function skCloud(x: number, y: number, scale = 1): React.ReactNode {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkEllipse cx={0} cy={0} rx={26} ry={16} fill="#ffffff" seed={710} />
      <SkEllipse cx={22} cy={6} rx={20} ry={13} fill="#ffffff" seed={711} />
      <SkEllipse cx={-22} cy={6} rx={16} ry={11} fill="#ffffff" seed={712} />
    </g>
  );
}

export function skTree(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const sway = Math.sin(t * 1.2 + x * 0.05) * 3;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkRect x={-6} y={-46} w={12} h={46} fill="#92400e" seed={720} />
      <g transform={`translate(${sway} 0)`}>
        <SkCircle cx={0} cy={-58} r={30} fill="#22c55e" seed={721} />
        <SkCircle cx={-18} cy={-46} r={18} fill="#16a34a" seed={722} />
        <SkCircle cx={18} cy={-46} r={18} fill="#16a34a" seed={723} />
      </g>
    </g>
  );
}

export function skFlower(x: number, y: number, t: number, scale = 1, color = "#fb7185"): React.ReactNode {
  const sway = Math.sin(t * 1.5 + x * 0.06) * 4;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkLine x1={0} y1={0} x2={sway} y2={-28} stroke="#16a34a" strokeWidth={3} seed={730} />
      <g transform={`translate(${sway} -30)`}>
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * Math.PI * 2;
          return <SkEllipse key={i} cx={Math.cos(a) * 8} cy={Math.sin(a) * 8} rx={5} ry={7} fill={color} seed={731 + i} />;
        })}
        <SkCircle cx={0} cy={0} r={5} fill="#fde047" seed={739} />
      </g>
    </g>
  );
}

export function skHouse(x: number, y: number, scale = 1): React.ReactNode {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkRect x={-50} y={-72} w={100} h={72} fill="#fcd34d" seed={740} />
      <SkPolygon points={[[-58, -72], [0, -116], [58, -72]]} fill="#ef4444" seed={741} />
      <SkRect x={-14} y={-42} w={28} h={42} fill="#7c3aed" seed={742} />
      <SkRect x={18} y={-62} w={22} h={22} fill="#bae6fd" seed={743} />
    </g>
  );
}

/** A simple person that can wave (pose drives the right arm). */
export function skPerson(x: number, y: number, t: number, scale = 1, wave = false): React.ReactNode {
  const armWave = wave ? Math.sin(t * 5) * 22 : 0;
  const skin = "#fcd34d";
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <SkLine x1={0} y1={-40} x2={0} y2={-12} seed={750} />
      <SkCircle cx={0} cy={-50} r={10} fill={skin} seed={751} />
      <SkLine x1={0} y1={-32} x2={-11} y2={-20} seed={752} />
      <g transform={`rotate(${armWave} 0 -32)`}>
        <SkLine x1={0} y1={-32} x2={12} y2={wave ? -46 : -20} seed={753} />
      </g>
      <SkLine x1={0} y1={-12} x2={-7} y2={0} seed={754} />
      <SkLine x1={0} y1={-12} x2={7} y2={0} seed={755} />
    </g>
  );
}
